import { PurchaseOrderModel } from '@/models/purchase_order';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import type { PurchaseListItem } from '@/types/purchase-list';
import {
  effectiveTax,
  type ReviewLineEdits,
  type SeededTax,
} from '@/utils/purchase-order/reviewLineEdits';

export interface BuildPOParams {
  supplierId: number;
  supplierName?: string;
  locationId: number;
  poNumber?: string;
  orderDate?: string;
  expectedDeliveryDate?: string;
  items: PurchaseListItem[];
  status: 'draft' | 'confirmed';
  currencyCode: string;
  extra?: Record<string, unknown>;
  /** Supplier default tax seeded onto untouched lines (review-page parity). */
  seededTax?: SeededTax;
  /** Per-line user overrides (cost / supplier SKU / tax), keyed by item id. */
  lineEdits?: ReviewLineEdits;
}

export function buildPOPayload(params: BuildPOParams): Record<string, any> {
  const edits = params.lineEdits ?? {};
  const lineItems = params.items
    .filter((i) => Number(i.quantity) > 0)
    .map((i) => {
      const e = edits[i.id];
      const orderedQuantity = Number(i.quantity);
      const unitPrice = e?.unitCost ?? Number(i.unitCost);
      const lineItem: Record<string, any> = {
        variantId: i.variantId,
        productName: i.productName,
        variant: i.variant,
        sku: i.sku,
        supplierSku: e?.supplierSku ?? i.supplierSku,
        variantImage: i.variantImage,
        orderedQuantity,
        unitPrice,
        currencyCode: i.currencyCode,
      };

      // Effective tax: a user edit wins, else the supplier-default seed. A line
      // carries extra.financial when its rate is positive OR a tax type id is
      // set (zero-rated). An untaxed line omits extra entirely.
      const tax = effectiveTax(e, params.seededTax);
      const rate = tax.taxRate ?? 0;
      if (rate > 0 || tax.taxTypeId != null) {
        lineItem.extra = {
          financial: {
            ...(tax.taxTypeId != null ? { tax_type_id: tax.taxTypeId } : {}),
            ...(tax.taxTypeName ? { tax_type_name: tax.taxTypeName } : {}),
            tax_rate: rate,
            tax_amount: CurrencyHelper.roundForCurrency(
              orderedQuantity * unitPrice * rate,
              params.currencyCode,
            ),
          },
        };
      }

      return lineItem;
    });

  return {
    poNumber: params.poNumber ?? '',
    supplierId: params.supplierId,
    supplierName: params.supplierName,
    locationId: params.locationId,
    orderDate: params.orderDate,
    expectedDeliveryDate: params.expectedDeliveryDate,
    status: params.status,
    currencyCode: params.currencyCode,
    items: lineItems,
    extra: params.extra ?? {},
  };
}

export async function createPOForSupplier(params: BuildPOParams): Promise<PurchaseOrderModel> {
  const payload = buildPOPayload(params);
  const po = new PurchaseOrderModel(payload as any);
  await po.save();
  return po;
}
